import React, { useState, useRef } from 'react';
import { motion } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import { 
  ChevronRight, 
  ChevronLeft, 
  Check, 
  DollarSign, 
  Building2, 
  User, 
  FileText, 
  Lock, 
  Clock 
} from 'lucide-react';
import './EnhancedContactForm.css';

const countries = [
  { code: "", name: "Select Country" },
  { code: "US", name: "United States" },
  { code: "GB", name: "United Kingdom" },
  { code: "CA", name: "Canada" },
  { code: "AU", name: "Australia" },
  { code: "ZA", name: "South Africa" },
  { code: "KE", name: "Kenya" },
  { code: "NG", name: "Nigeria" },
  { code: "GH", name: "Ghana" },
  { code: "TZ", name: "Tanzania" },
  { code: "UG", name: "Uganda" },
  { code: "ET", name: "Ethiopia" },
  { code: "EG", name: "Egypt" },
  { code: "DE", name: "Germany" },
  { code: "FR", name: "France" },
  { code: "ES", name: "Spain" },
  { code: "IT", name: "Italy" },
  { code: "NL", name: "Netherlands" },
  { code: "IN", name: "India" },
  { code: "CN", name: "China" },
  { code: "JP", name: "Japan" },
  { code: "SG", name: "Singapore" },
  { code: "BR", name: "Brazil" },
  { code: "MX", name: "Mexico" },
  { code: "AE", name: "United Arab Emirates" },
  { code: "SA", name: "Saudi Arabia" },
];

const industries = [
  "Select Industry",
  "Real Estate & Property Development",
  "Manufacturing & Production",
  "Healthcare & Medical",
  "Technology & Software",
  "Retail & E-commerce",
  "Agriculture & Agribusiness",
  "Energy & Utilities",
  "Transportation & Logistics",
  "Hospitality & Tourism",
  "Financial Services",
  "Construction & Engineering",
  "Mining & Natural Resources",
  "Telecommunications",
  "Import & Export",
  "Other"
];

const services = [
  "Select a service",
  "Commercial Real Estate Financing",
  "Large Project Financing",
  "Business Expansion Capital",
  "Working Capital (Established Business)",
  "Equity Financing",
  "Agri-Business Financing",
  "Non-Recourse Financing"
];

const EnhancedContactForm = () => {
  const [currentStep, setCurrentStep] = useState(1);
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    professionalTitle: '',
    companyName: '',
    industry: '',
    yearsInOperation: '',
    city: '',
    country: '',
    service: '',
    financingAmount: '',
    projectType: '',
    timeline: '',
    financingStage: '',
    description: '',
    hearAboutUs: ''
  });

  const [errors, setErrors] = useState({});
  const [touched, setTouched] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [submitError, setSubmitError] = useState('');

  const { ref, inView } = useInView({
    threshold: 0.1,
    triggerOnce: true
  });

  // Ref for the form card to scroll to it
  const formCardRef = useRef(null);

  const formatCurrency = (value) => {
    const numValue = value.replace(/[^0-9]/g, '');
    if (!numValue) return '';
    return parseInt(numValue).toLocaleString('en-US');
  };

  const handleAmountChange = (e) => {
    const formatted = formatCurrency(e.target.value);
    setFormData({ ...formData, financingAmount: formatted });
    setTouched({ ...touched, financingAmount: true });
    if (errors.financingAmount) {
      setErrors({ ...errors, financingAmount: '' });
    }
  };

  const setQuickAmount = (amount) => {
    setFormData({ ...formData, financingAmount: amount.toLocaleString('en-US') });
    setTouched({ ...touched, financingAmount: true });
    if (errors.financingAmount) {
      setErrors({ ...errors, financingAmount: '' });
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
    setTouched({ ...touched, [name]: true });
    
    if (errors[name]) {
      setErrors({ ...errors, [name]: '' });
    }
  };

  const validateStep = (step) => {
    const newErrors = {};
    
    if (step === 1) {
      if (!formData.firstName.trim()) {
        newErrors.firstName = 'First name is required';
      } else if (formData.firstName.trim().length < 2) {
        newErrors.firstName = 'First name must be at least 2 characters';
      }
      
      if (!formData.lastName.trim()) {
        newErrors.lastName = 'Last name is required';
      } else if (formData.lastName.trim().length < 2) {
        newErrors.lastName = 'Last name must be at least 2 characters';
      }
      
      if (!formData.email.trim()) {
        newErrors.email = 'Email is required';
      } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
        newErrors.email = 'Please enter a valid email address';
      }
      
      if (!formData.phone.trim()) {
        newErrors.phone = 'Phone number is required';
      } else if (formData.phone.trim().length < 8) {
        newErrors.phone = 'Please enter a valid phone number';
      }
    }
    
    if (step === 2) {
      if (!formData.companyName.trim()) {
        newErrors.companyName = 'Company name is required';
      } else if (formData.companyName.trim().length < 2) {
        newErrors.companyName = 'Company name must be at least 2 characters';
      }
      
      if (!formData.industry || formData.industry === 'Select Industry') {
        newErrors.industry = 'Please select an industry';
      }
      
      if (!formData.country || formData.country === '') {
        newErrors.country = 'Please select a country';
      }
    }
    
    if (step === 3) {
      if (!formData.service || formData.service === 'Select a service') {
        newErrors.service = 'Please select a service';
      }
      
      if (!formData.financingAmount) {
        newErrors.financingAmount = 'Financing amount is required';
      } else {
        const amount = parseInt(formData.financingAmount.replace(/,/g, ''));
        if (isNaN(amount) || amount < 100000) {
          newErrors.financingAmount = 'Minimum financing amount is $100,000';
        }
      }
      
      if (!formData.timeline) {
        newErrors.timeline = 'Please select a timeline';
      }
    }
    
    if (step === 4) {
      if (!formData.description.trim()) {
        newErrors.description = 'Project description is required';
      } else if (formData.description.trim().length < 50) {
        newErrors.description = 'Please provide at least 50 characters describing your project';
      } else if (formData.description.trim().length > 2000) {
        newErrors.description = 'Description must not exceed 2000 characters';
      }
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const nextStep = () => {
    if (validateStep(currentStep)) {
      setCurrentStep(prev => Math.min(prev + 1, 4));
      // Scroll to the form card instead of top of page
      if (formCardRef.current) {
        formCardRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    }
  };

  const prevStep = () => {
    setCurrentStep(prev => Math.max(prev - 1, 1));
    // Scroll to the form card instead of top of page
    if (formCardRef.current) {
      formCardRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };

 const handleSubmit = async (e) => {
  e.preventDefault();
  
  if (!validateStep(4)) return;
  
  setIsSubmitting(true);
  setSubmitError('');
  
  try {
    // Better API URL configuration
    const API_URL = process.env.REACT_APP_API_URL ||
                    'https://email-server.nehlmac4.workers.dev/api/email/contact';
    
    const submissionData = {
      firstName: formData.firstName.trim(),
      lastName: formData.lastName.trim(),
      email: formData.email.trim(),
      phone: formData.phone.trim(),
      professionalTitle: formData.professionalTitle.trim(),
      companyName: formData.companyName.trim(),
      industry: formData.industry,
      yearsInOperation: formData.yearsInOperation,
      city: formData.city.trim(),
      country: formData.country,
      service: formData.service,
      financingAmount: formData.financingAmount,
      projectType: formData.projectType.trim(),
      timeline: formData.timeline,
      financingStage: formData.financingStage,
      description: formData.description.trim(),
      hearAboutUs: formData.hearAboutUs,
      validationPassed: true,
      timestamp: new Date().toISOString()
    };
      
    const response = await fetch(API_URL, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(submissionData),
    });

    const contentType = response.headers.get("content-type");

    if (contentType && contentType.includes("application/json")) {
      const data = await response.json();

      if (response.ok && data.success) {
        setIsSubmitted(true);

        // Google Ads conversion tracking
        if (window.gtag) {
          window.gtag('event', 'conversion', {
            'send_to': 'AW-16898712872/nV9ECMGRkrsaEKjK9_k-'
          });
          window.gtag('event', 'conversion', {
            'send_to': 'AW-17770054617/SxiMCNOYwN4bENmHtplC'
          });
        }

        // Also track with Facebook Pixel if available
        if (window.fbq) {
          window.fbq('track', 'Lead', {
            content_name: 'Commercial Financing Application',
            value: parseFloat(formData.financingAmount.replace(/,/g, '')) || 0,
            currency: 'USD'
          });
        }

        // Reset form
        setFormData({
          firstName: '', lastName: '', email: '', phone: '', professionalTitle: '',
          companyName: '', industry: '', yearsInOperation: '', city: '', country: '',
          service: '', financingAmount: '', projectType: '', timeline: '', financingStage: '',
          description: '', hearAboutUs: ''
        });
        
        setTouched({});
        setErrors({});
        setCurrentStep(1);
      } else {
        throw new Error(data.message || 'Failed to submit form. Please try again.');
      }
    } else {
      throw new Error('Server responded with non-JSON format. Please contact support.');
    }
  } catch (error) {
    console.error('Form submission error:', error);
    
    // More specific error messages
    if (error.name === 'TypeError' && error.message.includes('fetch')) {
      setSubmitError('Network error. Please check your internet connection and try again.');
    } else if (error.message.includes('Server')) {
      setSubmitError('Server error. Our team has been notified. Please try again later.');
    } else {
      setSubmitError(error.message || 'Failed to send your message. Please try again later.');
    }
  } finally {
    setIsSubmitting(false);
  }
};

  const getStepIcon = (step) => {
    switch(step) {
      case 1: return <User className="step-icon" />;
      case 2: return <Building2 className="step-icon" />;
      case 3: return <DollarSign className="step-icon" />;
      case 4: return <FileText className="step-icon" />;
      default: return null;
    }
  };

  if (isSubmitted) {
    return (
      <section className="enhanced-contact-section" ref={ref}>
        <div className="enhanced-contact-container">
          <motion.div 
            className="success-card"
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.5 }}
          >
            <div className="success-icon-wrapper">
              <Check className="success-icon" />
            </div>
            <h2 className="success-title">Thank You!</h2>
            <p className="success-message">
              We've received your financing inquiry and will review it shortly.
            </p>
            <div className="next-steps-card">
              <h3 className="next-steps-title">What happens next?</h3>
              <ul className="next-steps-list">
                <li>
                  <Check className="check-icon" />
                  <span>Our team will review your application within 24 hours</span>
                </li>
                <li>
                  <Check className="check-icon" />
                  <span>We'll contact you to discuss your financing needs in detail</span>
                </li>
                <li>
                  <Check className="check-icon" />
                  <span>You'll receive a preliminary assessment of your project</span>
                </li>
              </ul>
            </div>
            <button
              onClick={() => setIsSubmitted(false)}
              className="submit-another-btn"
            >
              Submit Another Inquiry
            </button>
          </motion.div>
        </div>
      </section>
    );
  }

  return (
    <section id="contact" className="enhanced-contact-section" ref={ref}>
      <div className="enhanced-contact-container">
        <motion.div 
          className="enhanced-contact-header"
          initial={{ opacity: 0, y: 20 }}
          animate={inView ? { opacity: 1, y: 0 } : { opacity: 0, y: 20 }}
          transition={{ duration: 0.8 }}
        >
          <h2 className="enhanced-contact-title">Commercial Financing Application</h2>
          <p className="enhanced-contact-subtitle">
            Tell us about your financing needs and we'll get back to you within 24 hours
          </p>
        </motion.div>

        <div className="enhanced-contact-grid">
          <motion.div 
            className="form-column"
            initial={{ opacity: 0, x: -20 }}
            animate={inView ? { opacity: 1, x: 0 } : { opacity: 0, x: -20 }}
            transition={{ duration: 0.8, delay: 0.2 }}
          >
            <div className="form-card" ref={formCardRef}>
              <div className="progress-header">
                <div className="progress-steps">
                  {[1, 2, 3, 4].map((step) => (
                    <React.Fragment key={step}>
                      <div className={`step-circle ${
                        currentStep > step ? 'step-completed' :
                        currentStep === step ? 'step-active' :
                        'step-inactive'
                      }`}>
                        {currentStep > step ? <Check className="step-icon" /> : getStepIcon(step)}
                      </div>
                      {step < 4 && (
                        <div className={`step-line ${currentStep > step ? 'line-completed' : 'line-inactive'}`} />
                      )}
                    </React.Fragment>
                  ))}
                </div>
                <p className="step-label">
                  Step {currentStep} of 4: {
                    currentStep === 1 ? 'Personal Information' :
                    currentStep === 2 ? 'Business Details' :
                    currentStep === 3 ? 'Financing Request' :
                    'Project Description'
                  }
                </p>
              </div>

              <form onSubmit={handleSubmit} className="enhanced-form">
                {currentStep === 1 && (
                  <div className="form-step">
                    <div className="form-row">
                      <div className="form-group">
                        <label className="form-label">
                          First Name <span className="required">*</span>
                        </label>
                        <input
                          type="text"
                          name="firstName"
                          value={formData.firstName}
                          onChange={handleChange}
                          className={`form-input ${errors.firstName && touched.firstName ? 'input-error' : ''}`}
                          placeholder="John"
                        />
                        {errors.firstName && touched.firstName && (
                          <p className="error-message">{errors.firstName}</p>
                        )}
                      </div>
                      
                      <div className="form-group">
                        <label className="form-label">
                          Last Name <span className="required">*</span>
                        </label>
                        <input
                          type="text"
                          name="lastName"
                          value={formData.lastName}
                          onChange={handleChange}
                          className={`form-input ${errors.lastName && touched.lastName ? 'input-error' : ''}`}
                          placeholder="Smith"
                        />
                        {errors.lastName && touched.lastName && (
                          <p className="error-message">{errors.lastName}</p>
                        )}
                      </div>
                    </div>

                    <div className="form-row">
                      <div className="form-group">
                        <label className="form-label">
                          Email Address <span className="required">*</span>
                        </label>
                        <input
                          type="email"
                          name="email"
                          value={formData.email}
                          onChange={handleChange}
                          className={`form-input ${errors.email && touched.email ? 'input-error' : ''}`}
                          placeholder="john.smith@company.com"
                        />
                        {errors.email && touched.email && (
                          <p className="error-message">{errors.email}</p>
                        )}
                      </div>
                      
                      <div className="form-group">
                        <label className="form-label">
                          Phone Number <span className="required">*</span>
                        </label>
                        <input
                          type="tel"
                          name="phone"
                          value={formData.phone}
                          onChange={handleChange}
                          className={`form-input ${errors.phone && touched.phone ? 'input-error' : ''}`}
                          placeholder="+1 (555) 000-0000"
                        />
                        {errors.phone && touched.phone && (
                          <p className="error-message">{errors.phone}</p>
                        )}
                      </div>
                    </div>

                    <div className="form-group">
                      <label className="form-label">
                        Professional Title/Role
                      </label>
                      <input
                        type="text"
                        name="professionalTitle"
                        value={formData.professionalTitle}
                        onChange={handleChange}
                        className="form-input"
                        placeholder="CEO, CFO, Director, etc."
                      />
                    </div>
                  </div>
                )}

                {currentStep === 2 && (
                  <div className="form-step">
                    <div className="form-group">
                      <label className="form-label">
                        Company Name <span className="required">*</span>
                      </label>
                      <input
                        type="text"
                        name="companyName"
                        value={formData.companyName}
                        onChange={handleChange}
                        className={`form-input ${errors.companyName && touched.companyName ? 'input-error' : ''}`}
                        placeholder="Your Company Name"
                      />
                      {errors.companyName && touched.companyName && (
                        <p className="error-message">{errors.companyName}</p>
                      )}
                    </div>

                    <div className="form-row">
                      <div className="form-group">
                        <label className="form-label">
                          Industry Sector <span className="required">*</span>
                        </label>
                        <select
                          name="industry"
                          value={formData.industry}
                          onChange={handleChange}
                          className={`form-input ${errors.industry && touched.industry ? 'input-error' : ''}`}
                        >
                          {industries.map(ind => (
                            <option key={ind} value={ind}>{ind}</option>
                          ))}
                        </select>
                        {errors.industry && touched.industry && (
                          <p className="error-message">{errors.industry}</p>
                        )}
                      </div>

                      <div className="form-group">
                        <label className="form-label">
                          Years in Operation
                        </label>
                        <select
                          name="yearsInOperation"
                          value={formData.yearsInOperation}
                          onChange={handleChange}
                          className="form-input"
                        >
                          <option value="">Select years</option>
                          <option value="<1">Less than 1 year</option>
                          <option value="1-3">1-3 years</option>
                          <option value="3-5">3-5 years</option>
                          <option value="5-10">5-10 years</option>
                          <option value="10+">10+ years</option>
                        </select>
                      </div>
                    </div>

                    <div className="form-row">
                      <div className="form-group">
                        <label className="form-label">City</label>
                        <input
                          type="text"
                          name="city"
                          value={formData.city}
                          onChange={handleChange}
                          className="form-input"
                          placeholder="Your City"
                        />
                      </div>

                      <div className="form-group">
                        <label className="form-label">
                          Country <span className="required">*</span>
                        </label>
                        <select
                          name="country"
                          value={formData.country}
                          onChange={handleChange}
                          className={`form-input ${errors.country && touched.country ? 'input-error' : ''}`}
                        >
                          {countries.map(c => (
                            <option key={c.code} value={c.code}>{c.name}</option>
                          ))}
                        </select>
                        {errors.country && touched.country && (
                          <p className="error-message">{errors.country}</p>
                        )}
                      </div>
                    </div>
                  </div>
                )}

                {currentStep === 3 && (
                  <div className="form-step">
                    <div className="form-group">
                      <label className="form-label">
                        Service Type <span className="required">*</span>
                      </label>
                      <select
                        name="service"
                        value={formData.service}
                        onChange={handleChange}
                        className={`form-input ${errors.service && touched.service ? 'input-error' : ''}`}
                      >
                        {services.map(s => (
                          <option key={s} value={s}>{s}</option>
                        ))}
                      </select>
                      {errors.service && touched.service && (
                        <p className="error-message">{errors.service}</p>
                      )}
                    </div>

                    <div className="form-group">
                      <label className="form-label">
                        Financing Amount (USD) <span className="required">*</span>
                      </label>
                      <div className="amount-input-wrapper">
                        <span className="currency-symbol">$</span>
                        <input
                          type="text"
                          name="financingAmount"
                          value={formData.financingAmount}
                          onChange={handleAmountChange}
                          className={`form-input amount-input ${errors.financingAmount && touched.financingAmount ? 'input-error' : ''}`}
                          placeholder="1,000,000"
                        />
                      </div>
                      {errors.financingAmount && touched.financingAmount && (
                        <p className="error-message">{errors.financingAmount}</p>
                      )}
                      <p className="helper-text">Minimum financing amount: $100,000</p>
                      
                      <div className="quick-amount-buttons">
                        <button type="button" onClick={() => setQuickAmount(500000)} className="quick-amount-btn">
                          $500K
                        </button>
                        <button type="button" onClick={() => setQuickAmount(1000000)} className="quick-amount-btn">
                          $1M
                        </button>
                        <button type="button" onClick={() => setQuickAmount(5000000)} className="quick-amount-btn">
                          $5M
                        </button>
                        <button type="button" onClick={() => setQuickAmount(10000000)} className="quick-amount-btn">
                          $10M
                        </button>
                        <button type="button" onClick={() => setQuickAmount(25000000)} className="quick-amount-btn">
                          $25M+
                        </button>
                      </div>
                    </div>

                    <div className="form-row">
                      <div className="form-group">
                        <label className="form-label">
                          Timeline/Urgency <span className="required">*</span>
                        </label>
                        <select
                          name="timeline"
                          value={formData.timeline}
                          onChange={handleChange}
                          className={`form-input ${errors.timeline && touched.timeline ? 'input-error' : ''}`}
                        >
                          <option value="">Select timeline</option>
                          <option value="1-3 months">1-3 months</option>
                          <option value="3-6 months">3-6 months</option>
                          <option value="6-12 months">6-12 months</option>
                          <option value="12+ months">12+ months</option>
                        </select>
                        {errors.timeline && touched.timeline && (
                          <p className="error-message">{errors.timeline}</p>
                        )}
                      </div>

                      <div className="form-group">
                        <label className="form-label">
                          Current Financing Stage
                        </label>
                        <select
                          name="financingStage"
                          value={formData.financingStage}
                          onChange={handleChange}
                          className="form-input"
                        >
                          <option value="">Select stage</option>
                          <option value="Planning">Planning Phase</option>
                          <option value="Ready">Ready to Proceed</option>
                          <option value="Urgent">Urgent Need</option>
                        </select>
                      </div>
                    </div>

                    <div className="form-group">
                      <label className="form-label">
                        Project/Asset Type
                      </label>
                      <input
                        type="text"
                        name="projectType"
                        value={formData.projectType}
                        onChange={handleChange}
                        className="form-input"
                        placeholder="e.g., Commercial Property, Equipment Purchase, Business Expansion"
                      />
                    </div>
                  </div>
                )}

                {currentStep === 4 && (
                  <div className="form-step">
                    <div className="form-group">
                      <label className="form-label">
                        Project Description <span className="required">*</span>
                      </label>
                      <textarea
                        name="description"
                        value={formData.description}
                        onChange={handleChange}
                        rows="8"
                        className={`form-textarea ${errors.description && touched.description ? 'input-error' : ''}`}
                        placeholder="Please provide details about your project, business background, intended use of funds, and any other relevant information..."
                      />
                      {errors.description && touched.description && (
                        <p className="error-message">{errors.description}</p>
                      )}
                      <p className="helper-text">
                        {formData.description.length} / 2000 characters (minimum 50 required)
                      </p>
                    </div>

                    <div className="form-group">
                      <label className="form-label">
                        How did you hear about us?
                      </label>
                      <select
                        name="hearAboutUs"
                        value={formData.hearAboutUs}
                        onChange={handleChange}
                        className="form-input"
                      >
                        <option value="">Select an option</option>
                        <option value="Google Search">Google Search</option>
                        <option value="Social Media">Social Media</option>
                        <option value="Referral">Referral</option>
                        <option value="LinkedIn">LinkedIn</option>
                        <option value="Industry Event">Industry Event</option>
                        <option value="Other">Other</option>
                      </select>
                    </div>

                    <div className="review-card">
                      <h4 className="review-title">Review Your Application</h4>
                      <div className="review-details">
                        <p><span className="review-label">Name:</span> {formData.firstName} {formData.lastName}</p>
                        <p><span className="review-label">Email:</span> {formData.email}</p>
                        <p><span className="review-label">Company:</span> {formData.companyName}</p>
                        <p><span className="review-label">Service:</span> {formData.service}</p>
                        <p><span className="review-label">Amount:</span> ${formData.financingAmount}</p>
                        <p><span className="review-label">Timeline:</span> {formData.timeline}</p>
                      </div>
                    </div>

                    {submitError && (
                      <div className="error-message" style={{ marginTop: '1rem', padding: '1rem', background: '#fee', borderRadius: '0.5rem' }}>
                        {submitError}
                      </div>
                    )}
                  </div>
                )}

                <div className="form-navigation">
                  {currentStep > 1 && (
                    <button
                      type="button"
                      onClick={prevStep}
                      className="nav-btn nav-btn-prev"
                    >
                      <ChevronLeft className="nav-icon" />
                      Previous
                    </button>
                  )}
                  
                  {currentStep < 4 ? (
                    <button
                      type="button"
                      onClick={nextStep}
                      className="nav-btn nav-btn-next"
                    >
                      Next Step
                      <ChevronRight className="nav-icon" />
                    </button>
                  ) : (
                    <button
                      type="submit"
                      disabled={isSubmitting}
                      className="nav-btn nav-btn-submit"
                    >
                      {isSubmitting ? (
                        <>
                          <div className="spinner"></div>
                          Submitting...
                        </>
                      ) : (
                        <>
                          Submit Application
                          <Check className="nav-icon" />
                        </>
                      )}
                    </button>
                  )}
                </div>
              </form>
            </div>
          </motion.div>

          <motion.div 
            className="sidebar-column"
            initial={{ opacity: 0, x: 20 }}
            animate={inView ? { opacity: 1, x: 0 } : { opacity: 0, x: 20 }}
            transition={{ duration: 0.8, delay: 0.4 }}
          >
            <div className="sidebar-sticky">
              <div className="sidebar-card">
                <h3 className="sidebar-card-title">Why Choose Us</h3>
                <div className="trust-items">
                  <div className="trust-item">
                    <div className="trust-icon trust-icon-blue">
                      <Lock className="icon" />
                    </div>
                    <div>
                      <h4 className="trust-item-title">Secure & Confidential</h4>
                      <p className="trust-item-text">Your information is encrypted and protected</p>
                    </div>
                  </div>
                  
                  <div className="trust-item">
                    <div className="trust-icon trust-icon-green">
                      <Clock className="icon" />
                    </div>
                    <div>
                      <h4 className="trust-item-title">24-Hour Response</h4>
                      <p className="trust-item-text">We typically respond within 24 hours</p>
                    </div>
                  </div>
                  
                  <div className="trust-item">
                    <div className="trust-icon trust-icon-orange">
                      <Check className="icon" />
                    </div>
                    <div>
                      <h4 className="trust-item-title">No Obligation</h4>
                      <p className="trust-item-text">Free consultation with no commitment required</p>
                    </div>
                  </div>
                </div>
              </div>

              <div className="sidebar-card sidebar-card-gradient">
                <h3 className="sidebar-card-title">What Happens Next?</h3>
                <ol className="steps-list">
                  <li className="step-item">
                    <div className="step-number">1</div>
                    <p className="step-text">We review your application and assess your financing needs</p>
                  </li>
                  <li className="step-item">
                    <div className="step-number">2</div>
                    <p className="step-text">Our team contacts you to discuss details and requirements</p>
                  </li>
                  <li className="step-item">
                    <div className="step-number">3</div>
                    <p className="step-text">You receive a preliminary assessment and next steps</p>
                  </li>
                </ol>
              </div>

              <div className="sidebar-card">
                <h3 className="sidebar-card-title">Need Help?</h3>
                <div className="contact-info">
                  <div className="contact-info-item">
                    <p className="contact-info-label">Office Location</p>
                    <p className="contact-info-text">Johannesburg, South Africa</p>
                  </div>
                  <div className="contact-info-item">
                    <p className="contact-info-label">Response Time</p>
                    <p className="contact-info-text">Within 24 hours</p>
                  </div>
                  <div className="contact-info-item">
                    <p className="contact-info-label">Business Hours</p>
                    <p className="contact-info-text">Mon-Fri: 9:00 AM - 6:00 PM SAST</p>
                  </div>
                </div>
              </div>

              <div className="sidebar-card sidebar-card-stats">
                <h3 className="sidebar-card-title-white">Our Track Record</h3>
                <div className="stats-grid">
                  <div className="stat-item">
                    <p className="stat-number">$2.5B+</p>
                    <p className="stat-label">In financing facilitated</p>
                  </div>
                  <div className="stat-item">
                    <p className="stat-number">500+</p>
                    <p className="stat-label">Projects funded</p>
                  </div>
                  <div className="stat-item">
                    <p className="stat-number">50+</p>
                    <p className="stat-label">Countries served</p>
                  </div>
                </div>
              </div>
            </div>
          </motion.div>
        </div>

        <div className="privacy-note">
          <p>🔒 We respect your privacy. Your information is secure and will never be shared with third parties.</p>
        </div>
      </div>
    </section>
  );
};

export default EnhancedContactForm;